<template>
  <Transition name="modal-animation">
    <div v-show="modalActive" class="modal" aria-modal="true" role="dialog" :aria-label="title">
      <Transition name="modal-animation-inner">
        <div v-show="modalActive" class="modal-content">
          <header class="modal-title">
            <p>{{ title }}</p>
            <button
              v-if="model?.closeButtonVisible ?? true"
              type="button"
              class="icon-button"
              @click="handleClose"
              :disabled="closeDisabled"
              :aria-label="t('modal.closeIcon.alt')"
              :title="t('modal.closeIcon.alt')"
            >
              <img
                :src="appStore.currentMode() === DisplayMode.Light ? xCloseLight : xCloseDark"
                class="icon"
              />
            </button>
          </header>

          <div class="modal-body">
            <slot />
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { toRef, Transition } from 'vue'
import { t } from '../composables/Translation'
import xCloseLight from '../assets/icons/light/icon-x-close.svg'
import xCloseDark from '../assets/icons/dark/icon-x-close.svg'
import type { Modal } from '../types/configuration/Modal'
import { DisplayMode } from '../types/app/displayMode'
import { useAppStore } from '../stores/appStore'
import { useScrollLock } from '../composables/ScrollLock'

const appStore = useAppStore()

const props = withDefaults(
  defineProps<{
    model: Modal | undefined
    modalActive: boolean
    title: string
    closeDisabled?: boolean
  }>(),
  { closeDisabled: false }
)

const emit = defineEmits<{ (e: 'close'): void }>()

useScrollLock(toRef(props, 'modalActive'))

function handleClose() {
  emit('close')
}
</script>

<style lang="scss" scoped>
.modal {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: 12px;
  background: var(--modal-background-color);
  backdrop-filter: blur(2px);
  overflow: auto;
}

.modal-content {
  position: relative;
  width: min(640px, calc(100vw - 48px));
  max-height: min(95vh, 1000px);
  min-height: 0;
  overflow: hidden;

  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(17, 24, 39, 0.28);

  display: flex;
  flex-direction: column;
}

.modal-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 16px;

  color: var(--text-color);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;

  position: sticky;
  top: 0;
  background: var(--background-color);
  z-index: 1;

  > p {
    margin: 0;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  padding-bottom: 20px;
  padding-left: 16px;
  padding-right: 16px;
}

.icon-button {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  transition:
    transform 120ms ease,
    background-color 120ms ease;
}
.icon-button:hover {
  background-color: rgba(59, 67, 82, 0.06);
}
.icon-button:active {
  transform: scale(0.96);
}
.icon-button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.icon {
  width: 20px;
  height: 20px;
  display: block;
}

.modal-animation-enter-active,
.modal-animation-leave-active {
  transition: opacity 180ms ease;
}
.modal-animation-enter-from,
.modal-animation-leave-to {
  opacity: 0;
}

.modal-animation-inner-enter-active,
.modal-animation-inner-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}
.modal-animation-inner-enter-from,
.modal-animation-inner-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.98);
}

@media (max-width: 992px) {
  .modal {
    padding: 0;
    display: flex;
    align-items: stretch;
    justify-content: center;
  }

  .modal-content {
    width: 100vw;
    height: 100dvh;
    max-height: none;
    border-radius: 0;
    border: none;
    box-shadow: none;

    display: flex;
    flex-direction: column;
    min-height: 0;
    padding-bottom: env(safe-area-inset-bottom, 0);
  }

  .modal-title {
    flex-shrink: 0;
  }

  .modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding-top: 8px;
  }
}
</style>
